import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { FormTextFieldSkeleton } from "components/Skeleton";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import {
  UniversalWasteCategory,
  UpdateWasteStreamInput,
  WasteClassification,
  WasteKind,
} from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { Suspense, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { prettyPrintUniversalWasteCategory } from "../prettyPrintUniversalWasteCategory";
import { prettyPrintWasteClassification } from "../prettyPrintWasteClassification";
import { prettyPrintWasteKind } from "../prettyPrintWasteKind";
import { ProvisionalWasteStreamChip } from "../ProvisionalWasteStreamChip";
import { useSetWasteKind, useUpdateWasteStream, useWasteStream } from "./api";

const WASTE_KIND_SELECT_ITEMS = Object.values(WasteKind).map((value) => ({
  value,
  display: prettyPrintWasteKind(value),
}));

const NAME_RULES = {
  validate: (value: string | boolean | null) =>
    (typeof value === "string" && value.trim() !== "") || "Waste stream name is required",
};

const UNIVERSAL_WASTE_CATEGORY_SELECT_ITEMS = Object.values(UniversalWasteCategory).map(
  (value) => ({
    value,
    display: prettyPrintUniversalWasteCategory(value),
  })
);

/**
 * Sends the stream-field update when at least one stream field is dirty, and
 * skips it otherwise. The resolver rejects an id-only input ("at least one of
 * ... must be provided"), and a kind-only edit leaves every stream field
 * clean, so a kind-only save must not call the mutation at all.
 *
 * `ok` is false when the caller should stop `performUpdate` entirely (the
 * mutation ran but came back without data).
 *
 * `committed` says whether this call actually wrote anything, which `ok` alone
 * cannot: it is true for a save that succeeded and false when no stream field
 * changed. The waste kind override runs as a second mutation after this one and
 * can be refused on its own, so what the user is told about that refusal depends
 * on whether these fields are already persisted.
 */
async function performStreamUpdate({
  dirty,
  data,
  wasteStreamId,
  input,
  mutateUpdateWasteStream,
}: {
  dirty: Partial<Record<keyof UpdateWasteForm, boolean>>;
  data: UpdateWasteForm;
  wasteStreamId: string;
  input: UpdateWasteStreamInput;
  mutateUpdateWasteStream: ReturnType<typeof useUpdateWasteStream>[0];
}): Promise<{ ok: boolean; committed: boolean }> {
  const hasStreamFieldChanges =
    dirty.name ||
    dirty.comment ||
    dirty.classification ||
    dirty.formCode ||
    dirty.sourceCode ||
    dirty.willNotReport;

  if (!hasStreamFieldChanges) return { ok: true, committed: false };

  const result = await mutateUpdateWasteStream({
    update: (cache, { data: responseData }) => {
      if (responseData?.updateWasteStream) {
        // Write the same values we sent so the cache matches what the
        // server stored — refetchQueries will reconcile any drift.
        cache.modify({
          id: `WasteStream:${wasteStreamId}`,
          fields: {
            ...(dirty.name && { name: () => data.name.trim() }),
            ...(dirty.comment && {
              comment: () => data.comment?.trim() || null,
            }),
            ...(dirty.classification && {
              classification: () => data.classification ?? null,
            }),
            ...(dirty.formCode && {
              formCode: () => data.formCode?.trim() || null,
            }),
            ...(dirty.sourceCode && {
              sourceCode: () => data.sourceCode?.trim() || null,
            }),
            ...(dirty.willNotReport && { willNotReport: () => data.willNotReport }),
          },
        });
      }
    },
    variables: { input },
  });
  if (result.errors) {
    throw new Error(result.errors[0].message);
  }
  return { ok: Boolean(result.data?.updateWasteStream), committed: true };
}

/**
 * What the Waste Kind select says under itself.
 *
 * The stored kind reads the same whether a person pinned it or the derivation
 * produced it, so without this a staff user reopening the drawer cannot tell an
 * override is in force, and the clear affordance looks like it does nothing.
 */
function wasteKindHelperText(overriddenWasteKind: WasteKind | null): string {
  const base =
    "Moves this stream's lines onto a stream with the chosen kind, which changes its " +
    "containers' accumulation clock. It does not change whether the waste is reportable — " +
    "use the Reportable toggle for that.";
  if (overriddenWasteKind === null) {
    return `Staff override. ${base}`;
  }
  return (
    `Staff override, currently set to ${prettyPrintWasteKind(overriddenWasteKind)}. ` +
    `Clear it to go back to the kind the manifest data derives. ${base}`
  );
}

/**
 * Whether a failed mutation failed because the request never got an answer,
 * rather than because the server refused it.
 *
 * Apollo reports the two differently: a refusal arrives as `graphQLErrors`
 * carrying the resolver's message, and a dropped or timed-out request arrives
 * with `networkError` set and no GraphQL errors at all. Only the second is
 * worth retrying, and only the first should be described to the user as a
 * refusal.
 */
function isNetworkFailure(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  const apollo: { networkError?: unknown; graphQLErrors?: unknown[] } = error;
  return Boolean(apollo.networkError) && (apollo.graphQLErrors?.length ?? 0) === 0;
}

/**
 * Whether this user gets the waste-kind override control. Mirrors the
 * setWasteKind authorizer, which is and(isStaff, container tracking).
 *
 * The flag half matters as much as the staff half: everything the override
 * visibly changes — the container's deadline, its clock basis, the satellite
 * caps — lives behind container tracking. Without the flag the control would
 * offer staff a correction whose effect the tenant has no screen for, and the
 * server would refuse the mutation anyway.
 */
function useCanOverrideWasteKind() {
  const { isStaff } = useCurrentUser();
  const { featureFlags } = useFeatureFlags();
  return isStaff && Boolean(featureFlags[FeatureFlagKey.ContainerTracking]);
}

/**
 * Fires the staff waste-kind override after a successful stream update, but
 * only when the value actually moved. It's a separate mutation from the
 * stream update because it fans out over the stream's lines rather than
 * updating the stream row.
 *
 * The form always reloads with a null category (the stream query has nowhere
 * to read a stored one from), so a stream that was already Universal waste
 * looks unchanged on kind alone. Picking a category on that unchanged kind is
 * still a correction the staff user needs applied, so the category is checked
 * on its own as well as alongside the kind.
 */
async function submitWasteKindOverride({
  canOverrideWasteKind,
  wasteStreamId,
  wasteKind,
  originalWasteKind,
  universalWasteCategory,
  setWasteKind,
}: {
  canOverrideWasteKind: boolean;
  wasteStreamId: string;
  wasteKind: WasteKind | null;
  originalWasteKind: WasteKind | null;
  universalWasteCategory: UniversalWasteCategory | null;
  setWasteKind: (options: {
    variables: {
      wasteStreamId: string;
      kind: WasteKind | null;
      universalWasteCategory: UniversalWasteCategory | null;
    };
  }) => Promise<unknown>;
}) {
  const isUniversalWaste = wasteKind === WasteKind.UniversalWaste;
  const kindChanged = wasteKind !== originalWasteKind;
  const categoryChanged = isUniversalWaste && universalWasteCategory != null;
  if (!canOverrideWasteKind || (!kindChanged && !categoryChanged)) return false;
  await setWasteKind({
    variables: {
      wasteStreamId,
      kind: wasteKind,
      universalWasteCategory: isUniversalWaste ? universalWasteCategory : null,
    },
  });
  return true;
}

interface EditWasteStreamDialogProps {
  open: boolean;
  onClose: () => void;
  wasteStreamId: string;
}
interface UpdateWasteForm {
  name: string;
  comment: string;
  classification: WasteClassification | null;
  formCode: string;
  sourceCode: string;
  willNotReport: boolean;
  wasteKind: WasteKind | null;
  universalWasteCategory: UniversalWasteCategory | null;
}

interface EditWasteStreamFormProps {
  wasteStreamId: string;
  onClose: () => void;
}

const EditWasteStreamForm = ({ wasteStreamId, onClose }: EditWasteStreamFormProps) => {
  const {
    handleSubmit: handleSubmitForm,
    formState,
    reset,
    control,
    watch,
    setValue,
  } = useForm<UpdateWasteForm>({
    defaultValues: {
      name: "",
      comment: "",
      classification: null,
      formCode: "",
      sourceCode: "",
      willNotReport: false,
      wasteKind: null,
      universalWasteCategory: null,
    },
  });
  const alerts = useAlerts();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const canOverrideWasteKind = useCanOverrideWasteKind();

  // Fetch current waste stream data - no loading state needed with suspense
  const { data: wasteStreamData } = useWasteStream(wasteStreamId);

  // Update mutation
  const [mutateUpdateWasteStream, { loading: updatingStream }] = useUpdateWasteStream();
  const [setWasteKind, { loading: settingKind }] = useSetWasteKind();
  // A kind-only save runs no stream mutation, so gating Save on the stream
  // mutation alone left it live through the whole setWasteKind round trip — a
  // second click queued a second facility-wide forced run for one correction.
  const updating = updatingStream || settingKind;

  // Initialize form with current data
  useEffect(() => {
    if (wasteStreamData?.wasteStream) {
      reset({
        name: wasteStreamData.wasteStream.name,
        comment: wasteStreamData.wasteStream.comment ?? "",
        classification: wasteStreamData.wasteStream.classification || null,
        formCode: wasteStreamData.wasteStream.formCode ?? "",
        sourceCode: wasteStreamData.wasteStream.sourceCode ?? "",
        willNotReport: wasteStreamData.wasteStream.willNotReport ?? false,
        wasteKind: wasteStreamData.wasteStream.wasteKind ?? null,
        // The stream query has no stored category to read back, so this is
        // always a blank input to the correction rather than a display of
        // current state.
        universalWasteCategory: null,
      });
    }
  }, [wasteStreamData, reset]);

  const wasteKindValue = watch("wasteKind");
  const originalWasteKind = wasteStreamData?.wasteStream?.wasteKind ?? null;
  const overriddenWasteKind = wasteStreamData?.wasteStream?.overriddenWasteKind ?? null;
  // The server refuses a category paired with any kind other than Universal
  // waste, so moving the select off Universal waste has to clear whatever
  // category was chosen alongside it.
  useEffect(() => {
    if (wasteKindValue !== WasteKind.UniversalWaste) {
      setValue("universalWasteCategory", null);
    }
  }, [wasteKindValue, setValue]);

  const performUpdate = async (data: UpdateWasteForm) => {
    // Only send the fields the user actually changed. The resolver branches on
    // formCode/sourceCode presence to fire the code cascade onto raws and
    // vendor lines, so a willNotReport-only save must not include those keys.
    const dirty = formState.dirtyFields;
    const input: UpdateWasteStreamInput = {
      id: wasteStreamId,
    };
    if (dirty.name) input.name = data.name.trim();
    // `FormTextField` turns a cleared input into `null`, so `?.trim()` alone
    // would yield `undefined` and the field would be stripped from the
    // mutation. `|| null` lets the resolver actually clear the value.
    if (dirty.comment) input.comment = data.comment?.trim() || null;
    if (dirty.classification) input.classification = data.classification;
    if (dirty.formCode) input.formCode = data.formCode?.trim() || null;
    if (dirty.sourceCode) input.sourceCode = data.sourceCode?.trim() || null;
    if (dirty.willNotReport) input.willNotReport = data.willNotReport;

    try {
      const streamUpdateSucceeded = await performStreamUpdate({
        dirty,
        data,
        wasteStreamId,
        input,
        mutateUpdateWasteStream,
      });
      if (!streamUpdateSucceeded.ok) {
        return;
      }
      let kindQueued = false;
      try {
        kindQueued = await submitWasteKindOverride({
          canOverrideWasteKind,
          wasteStreamId,
          wasteKind: data.wasteKind,
          originalWasteKind,
          universalWasteCategory: data.universalWasteCategory,
          setWasteKind,
        });
      } catch (kindError) {
        // A network failure is not a refusal. The server refuses for reasons a
        // retry cannot change — an attached profile, a registration, a merged
        // stream — but a dropped request says nothing about the stream, so it
        // keeps the dialog open and the wording neutral rather than telling the
        // user their correction was rejected.
        if (isNetworkFailure(kindError)) {
          alerts.error("Could not reach the server to apply the waste kind change", kindError);
          return;
        }
        // The stream fields are a separate mutation and have already committed
        // by this point, so reporting the whole save as failed would invite a
        // retry that repeats them. The dialog closes on what did save and names
        // what did not.
        //
        // The error is passed whole, not as a message string: the alert renders
        // `err.message` and copies `err.stack`, so a string argument would show
        // the headline with no reason under it and copy nothing.
        if (streamUpdateSucceeded.committed) {
          alerts.error("Waste stream updated, but the waste kind change was refused", kindError);
          onClose();
          return;
        }
        alerts.error("The waste kind change was refused", kindError);
        return;
      }
      // A kind correction is applied by a background run, not by the mutation:
      // the lines re-resolve onto a stream carrying the new kind. Until that run
      // finishes this stream still reads the old kind, so saying "updated" would
      // send the user back to a drawer showing the value they just changed.
      if (kindQueued) {
        alerts.success(
          "Waste stream updated. The waste kind change is being applied and will appear once processing finishes."
        );
      } else {
        alerts.success("Waste stream updated");
      }
      onClose();
    } catch (error) {
      if (error instanceof Error) {
        alerts.error("Failed to update waste stream: " + error.message);
      } else {
        alerts.error("Failed to update waste stream", "An unknown error occurred");
      }
    }
  };

  const onSubmit = async (data: UpdateWasteForm) => {
    // Check if classification has changed
    const originalClassification = wasteStreamData?.wasteStream?.classification || null;
    const classificationChanged = originalClassification !== data.classification;

    if (classificationChanged) {
      // Show confirmation dialog and break out
      setShowConfirmation(true);
      return;
    }

    // Proceed with update
    await performUpdate(data);
  };

  // Handler for when user confirms the classification change
  const handleConfirmSubmit = async () => {
    const submitter = handleSubmitForm(performUpdate);
    await submitter();
    setShowConfirmation(false);
  };

  const handleClose = () => {
    if (!updating) {
      setShowConfirmation(false);
      onClose();
    }
  };

  const handleCancelConfirmation = () => {
    handleClose();
  };

  return (
    <>
      <DialogContent>
        {wasteStreamData?.wasteStream?.isProvisional && (
          <Box sx={{ pt: 1 }}>
            <ProvisionalWasteStreamChip isProvisional />
          </Box>
        )}
        <Stack spacing={2} sx={{ pt: 1 }}>
          <FormTextField name="name" label="Name" fullWidth control={control} rules={NAME_RULES} />
          <FormTextField
            name="formCode"
            label="Form Code"
            fullWidth
            control={control}
            helperText="Updates the stream-level form code. Cascades to vendor-sourced line items; eManifest-only line item data is preserved."
          />
          <FormTextField
            name="sourceCode"
            label="Source Code"
            fullWidth
            control={control}
            helperText="Updates the stream-level source code. Cascades to vendor-sourced line items; eManifest-only line item data is preserved."
          />
          <FormTextField name="comment" label="Note" fullWidth control={control} />
          <FormSelect
            name="classification"
            label="Classification"
            hideErrors
            selectItems={Object.values(WasteClassification).map((value) => ({
              value,
              display: prettyPrintWasteClassification(value),
            }))}
            control={control}
          />
          {canOverrideWasteKind && (
            <FormSelect
              name="wasteKind"
              label="Waste Kind"
              hideErrors
              helperText={wasteKindHelperText(overriddenWasteKind)}
              selectItems={WASTE_KIND_SELECT_ITEMS}
              control={control}
            />
          )}
          {canOverrideWasteKind && wasteKindValue === WasteKind.UniversalWaste && (
            <FormSelect
              name="universalWasteCategory"
              label="Universal Waste Category"
              // Required only when this save is what makes the stream universal
              // waste. A stream already on that kind reloads with the category
              // blank, because the stream query has none to read back, so
              // requiring it here would make an unrelated edit — a note, a form
              // code — impossible to save without re-picking a category the
              // user was not asked about.
              rules={{
                // Typed as the union `FormSelect` validates across every field
                // on this form, not as the category alone.
                validate: (value: string | boolean | null) =>
                  wasteKindValue === originalWasteKind || value != null
                    ? true
                    : "Select a universal waste category",
              }}
              helperText="The 40 CFR 273 one-year accumulation clock is gated on this category. The clock only changes if the generator's state adopted this category into its own universal waste program."
              selectItems={UNIVERSAL_WASTE_CATEGORY_SELECT_ITEMS}
              control={control}
            />
          )}
          <Controller
            name="willNotReport"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                control={<Switch checked={!value} onChange={(e) => onChange(!e.target.checked)} />}
                label={value ? "Not Reportable" : "Reportable"}
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={updating}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmitForm(onSubmit)}
          variant="contained"
          disabled={updating || !formState.isDirty}
        >
          {updating ? <CircularProgress size={20} /> : "Save"}
        </Button>
      </DialogActions>

      <ConfirmDialog
        open={showConfirmation}
        onClose={handleCancelConfirmation}
        onConfirm={handleConfirmSubmit}
        header="Are you sure you want to edit this waste stream classification?"
        resources={[
          {
            resourceType: "Waste Stream Name",
            resourceName: watch("name"),
          },
          {
            resourceType: "Previous Classification",
            resourceName: prettyPrintWasteClassification(
              wasteStreamData?.wasteStream?.classification
            ),
          },
          {
            resourceType: "New Classification",
            resourceName: prettyPrintWasteClassification(watch("classification")),
          },
        ]}
        info="Changing this classification will affect all of the line items in this waste stream."
        confirmText="Yes, Save Edits"
        cancelText="Cancel"
        loading={updating}
      />
    </>
  );
};

export const EditWasteStreamDialog = ({
  open,
  onClose,
  wasteStreamId,
}: EditWasteStreamDialogProps) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Waste Stream</DialogTitle>
      <Suspense
        fallback={
          <DialogContent>
            <Stack sx={{ pt: 1 }}>
              <FormTextFieldSkeleton />
              <FormTextFieldSkeleton />
              <FormTextFieldSkeleton />
              <FormTextFieldSkeleton />
              <FormTextFieldSkeleton />
            </Stack>
          </DialogContent>
        }
      >
        <EditWasteStreamForm wasteStreamId={wasteStreamId} onClose={onClose} />
      </Suspense>
    </Dialog>
  );
};
